/**
 * TUVI - Actualizar Bases
 * Página para re-sincronizar los precios de las cotizaciones BASE con la matriz de
 * actividades. Muestra una vista previa (valor actual vs. valor nuevo) por cotización
 * y permite aplicar a todas, a algunas o a un rango de consecutivos.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import './ActualizarBases.css';

const fmt = (n) => '$' + Math.round(Number(n) || 0).toLocaleString('es-CO');
const normalizar = (s) => (s || '').toString()
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase().trim().replace(/\s+/g, ' ');

const ETIQUETA_ESTADO = { base: 'Base', base_cotizador_web: 'Base Cotizador Web' };

export default function ActualizarBases({ onVolver }) {
  const { success, error } = useNotification();
  const [cargando, setCargando] = useState(true);
  const [aplicando, setAplicando] = useState(false);
  const [filas, setFilas] = useState([]);
  const [sel, setSel] = useState(() => new Set());

  // Filtros
  const [busqueda, setBusqueda] = useState('');
  const [soloConCambios, setSoloConCambios] = useState(true);
  const [estadosVer, setEstadosVer] = useState({ base: true, base_cotizador_web: true });

  // Selección por rango de consecutivo
  const [rangoDesde, setRangoDesde] = useState('');
  const [rangoHasta, setRangoHasta] = useState('');

  const [confirmar, setConfirmar] = useState(false);
  const ultimoClickRef = useRef(null); // índice del último clic, para Shift+clic

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const d = await client.query(`query($estados: [String!]) {
        basesParaActualizar(estados: $estados) {
          id consecutivo fecha estado nombre_cliente proyecto_nombre apartamento
          actividades_total actividades_cambian sin_matriz
          valor_final_actual valor_final_nuevo diferencia
        }
      }`, { estados: ['base', 'base_cotizador_web'] });
      setFilas(d.basesParaActualizar || []);
      setSel(new Set());
    } catch (e) {
      error('No se pudo cargar la vista previa de bases');
    } finally {
      setCargando(false);
    }
  }, [error]);

  useEffect(() => { cargar(); }, [cargar]);

  // Filas visibles según los filtros activos
  const visibles = useMemo(() => {
    const t = normalizar(busqueda);
    return filas.filter(f => {
      if (!estadosVer[f.estado]) return false;
      if (soloConCambios && f.actividades_cambian === 0) return false;
      if (!t) return true;
      return normalizar(`${f.consecutivo} ${f.nombre_cliente} ${f.proyecto_nombre} ${f.apartamento}`).includes(t);
    });
  }, [filas, busqueda, estadosVer, soloConCambios]);

  const idsVisibles = useMemo(() => visibles.map(f => f.id), [visibles]);
  const todasVisiblesSel = idsVisibles.length > 0 && idsVisibles.every(id => sel.has(id));

  const resumenSel = useMemo(() => {
    const elegidas = filas.filter(f => sel.has(f.id));
    return {
      n: elegidas.length,
      actividades: elegidas.reduce((s, f) => s + f.actividades_cambian, 0),
      diferencia: elegidas.reduce((s, f) => s + f.diferencia, 0),
    };
  }, [filas, sel]);

  const toggle = (id, idx, e) => {
    setSel(prev => {
      const n = new Set(prev);
      // Shift + clic selecciona todo el rango desde el clic anterior
      if (e && e.shiftKey && ultimoClickRef.current !== null) {
        const a = Math.min(ultimoClickRef.current, idx);
        const b = Math.max(ultimoClickRef.current, idx);
        const encender = !n.has(id);
        for (let i = a; i <= b; i++) {
          const fid = visibles[i] && visibles[i].id;
          if (fid == null) continue;
          if (encender) n.add(fid); else n.delete(fid);
        }
      } else if (n.has(id)) {
        n.delete(id);
      } else {
        n.add(id);
      }
      return n;
    });
    ultimoClickRef.current = idx;
  };

  const toggleTodasVisibles = () => {
    setSel(prev => {
      const n = new Set(prev);
      if (todasVisiblesSel) idsVisibles.forEach(id => n.delete(id));
      else idsVisibles.forEach(id => n.add(id));
      return n;
    });
  };

  const aplicarRango = () => {
    const d = parseInt(rangoDesde, 10);
    const h = parseInt(rangoHasta, 10);
    if (isNaN(d) && isNaN(h)) { error('Indica al menos un número de consecutivo'); return; }
    const min = isNaN(d) ? -Infinity : d;
    const max = isNaN(h) ? Infinity : h;
    const enRango = visibles.filter(f => f.consecutivo >= min && f.consecutivo <= max);
    if (!enRango.length) { error('Ninguna cotización visible en ese rango'); return; }
    setSel(prev => { const n = new Set(prev); enRango.forEach(f => n.add(f.id)); return n; });
    success(`${enRango.length} cotización(es) agregada(s) a la selección`);
  };

  const aplicar = async () => {
    setConfirmar(false);
    setAplicando(true);
    try {
      const ids = filas.filter(f => sel.has(f.id)).map(f => f.id);
      const r = await client.mutate(
        `mutation($ids: [Int!]!) { actualizarPreciosBases(ids: $ids) { success message cotizaciones_actualizadas actividades_actualizadas diferencia_total } }`,
        { ids }
      );
      const res = r.actualizarPreciosBases;
      if (res.success) success(res.message); else error(res.message);
      await cargar();
    } catch (e) {
      error('Error al actualizar los precios');
    } finally {
      setAplicando(false);
    }
  };

  const totalConCambios = filas.filter(f => f.actividades_cambian > 0).length;
  const signo = (v) => (v >= 0 ? '+' : '') + fmt(v);

  return (
    <div className="ab-page">
      <div className="ab-header">
        <div>
          <h2 className="ab-title">🔄 Actualizar Bases</h2>
          <p className="ab-sub">
            Aplica los precios vigentes de la <strong>matriz de actividades</strong> a las cotizaciones en estado <strong>Base</strong>.
            Se conservan las cantidades y el incremento global de cada cotización.
          </p>
        </div>
        <div className="ab-header-actions">
          <button className="ab-btn" onClick={cargar} disabled={cargando || aplicando}>↻ Recalcular</button>
          <button className="ab-btn" onClick={onVolver}>← Volver al listado</button>
        </div>
      </div>

      <div className="ab-cards">
        <div className="ab-card">
          <div className="ab-card-n">{filas.length}</div>
          <div className="ab-card-l">BASES EN TOTAL</div>
        </div>
        <div className="ab-card ab-card-amber">
          <div className="ab-card-n">{totalConCambios}</div>
          <div className="ab-card-l">CON CAMBIOS DE PRECIO</div>
        </div>
        <div className="ab-card ab-card-blue">
          <div className="ab-card-n">{resumenSel.n}</div>
          <div className="ab-card-l">SELECCIONADAS</div>
        </div>
        <div className={`ab-card ${resumenSel.diferencia >= 0 ? 'ab-card-green' : 'ab-card-red'}`}>
          <div className="ab-card-n ab-card-money">{signo(resumenSel.diferencia)}</div>
          <div className="ab-card-l">IMPACTO DE LA SELECCIÓN</div>
        </div>
      </div>

      <div className="ab-toolbar">
        <input className="ab-search" type="text" placeholder="Buscar por # consecutivo, cliente, proyecto o apto..."
          value={busqueda} onChange={e => setBusqueda(e.target.value)} />

        <label className="ab-check">
          <input type="checkbox" checked={estadosVer.base}
            onChange={e => setEstadosVer(v => ({ ...v, base: e.target.checked }))} /> Base
        </label>

        <label className="ab-check">
          <input type="checkbox" checked={estadosVer.base_cotizador_web}
            onChange={e => setEstadosVer(v => ({ ...v, base_cotizador_web: e.target.checked }))} /> Base Cotizador Web
        </label>

        <label className="ab-check">
          <input type="checkbox" checked={soloConCambios}
            onChange={e => setSoloConCambios(e.target.checked)} /> Solo con cambios
        </label>

        <div className="ab-rango">
          <span>Rango #</span>
          <input type="number" placeholder="desde" value={rangoDesde} onChange={e => setRangoDesde(e.target.value)} />
          <input type="number" placeholder="hasta" value={rangoHasta} onChange={e => setRangoHasta(e.target.value)} />
          <button className="ab-btn ab-btn-sm" onClick={aplicarRango}>Seleccionar rango</button>
        </div>

        {sel.size > 0 && (
          <button className="ab-btn ab-btn-sm" onClick={() => setSel(new Set())}>Limpiar selección</button>
        )}
      </div>

      {cargando ? (
        <div className="ab-empty">Calculando vista previa contra la matriz...</div>
      ) : visibles.length === 0 ? (
        <div className="ab-empty">
          {/* Distingue "todo al día" de "los filtros no dejan pasar nada": antes ambos
              casos mostraban el mismo mensaje y parecía que la página estuviera rota. */}
          {totalConCambios === 0 && filas.length > 0 ? (
            <>
              <p><strong>Todas las bases están al día con la matriz.</strong></p>
              <p className="ab-empty-nota">
                Ninguna de las {filas.length} cotizaciones base tiene diferencias de precio contra la matriz de
                actividades. Desmarca “Solo con cambios” si quieres verlas todas de todos modos.
              </p>
            </>
          ) : (
            <>
              <p>No hay cotizaciones base que coincidan con los filtros.</p>
              <p className="ab-empty-nota">Revisa el buscador o las casillas de estado.</p>
            </>
          )}
        </div>
      ) : (
        <div className="ab-tabla-wrap">
          <table className="ab-tabla">
            <thead>
              <tr>
                <th className="ab-col-check">
                  <input type="checkbox" checked={todasVisiblesSel} onChange={toggleTodasVisibles}
                    title="Seleccionar todas las visibles" />
                </th>
                <th>#</th>
                <th>FECHA</th>
                <th>CLIENTE</th>
                <th>PROYECTO</th>
                <th>APTO</th>
                <th>ESTADO</th>
                <th className="ab-num">ACTIVIDADES</th>
                <th className="ab-num">VALOR ACTUAL</th>
                <th className="ab-num">VALOR NUEVO</th>
                <th className="ab-num">DIFERENCIA</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((f, idx) => {
                const marcada = sel.has(f.id);
                return (
                  <tr key={f.id} className={marcada ? 'ab-row-sel' : ''}>
                    <td className="ab-col-check">
                      <input type="checkbox" checked={marcada} onChange={() => {}}
                        onClick={(e) => toggle(f.id, idx, e)} />
                    </td>
                    <td className="ab-cons">{f.consecutivo}</td>
                    <td>{f.fecha || '-'}</td>
                    <td>{f.nombre_cliente || '-'}</td>
                    <td>{f.proyecto_nombre || '-'}</td>
                    <td>{f.apartamento || '-'}</td>
                    <td><span className={`ab-badge ab-badge-${f.estado}`}>{ETIQUETA_ESTADO[f.estado] || f.estado}</span></td>
                    <td className="ab-num">
                      <strong>{f.actividades_cambian}</strong> <span className="ab-muted">/ {f.actividades_total}</span>
                      {f.sin_matriz > 0 && (
                        <span className="ab-warn" title="Actividades sin fila en la matriz: se conservan igual"> ⚠ {f.sin_matriz}</span>
                      )}
                    </td>
                    <td className="ab-num">{fmt(f.valor_final_actual)}</td>
                    <td className="ab-num">{fmt(f.valor_final_nuevo)}</td>
                    <td className={`ab-num ab-dif ${f.diferencia >= 0 ? 'ab-up' : 'ab-down'}`}>{signo(f.diferencia)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {resumenSel.n > 0 && (
        <div className="ab-barra">
          <span className="ab-barra-info">
            <strong>{resumenSel.n}</strong> cotización(es) · <strong>{resumenSel.actividades}</strong> actividad(es) a actualizar ·
            {' '}impacto <strong className={resumenSel.diferencia >= 0 ? 'ab-up' : 'ab-down'}>{signo(resumenSel.diferencia)}</strong>
          </span>
          <button className="ab-btn ab-btn-primary" onClick={() => setConfirmar(true)} disabled={aplicando}>
            {aplicando ? 'Actualizando...' : '✔ Actualizar seleccionadas'}
          </button>
        </div>
      )}

      {confirmar && (
        <div className="ab-overlay" onClick={() => setConfirmar(false)}>
          <div className="ab-modal" onClick={e => e.stopPropagation()}>
            <h3>Confirmar actualización</h3>
            <p>
              Se actualizarán los precios de <strong>{resumenSel.n}</strong> cotización(es) base
              (<strong>{resumenSel.actividades}</strong> actividades) con los valores vigentes de la matriz.
            </p>
            <p className="ab-modal-impacto">
              Impacto en el valor final:{' '}
              <strong className={resumenSel.diferencia >= 0 ? 'ab-up' : 'ab-down'}>{signo(resumenSel.diferencia)}</strong>
            </p>
            <p className="ab-modal-nota">
              Se conservan las cantidades y el incremento global de cada cotización. Esta acción no se puede deshacer.
            </p>
            <div className="ab-modal-acciones">
              <button className="ab-btn" onClick={() => setConfirmar(false)}>Cancelar</button>
              <button className="ab-btn ab-btn-primary" onClick={aplicar}>Sí, actualizar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
